import { useConfirm } from '@/contexts/ConfirmDialogContext';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import {
  Badge,
  Button,
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
  Field,
  Fieldset,
  Input,
  Label,
  Select,
  Text,
} from '@a7y/tailwind-catalyst';
import type { AdminOrganizationDetail, PlanFeatures, PlanId, PlanTier } from '@a7y/types';
import { PLAN_TIERS, maxPlanTier } from '@a7y/types';
import { useMutation } from '@tanstack/react-query';
import { Clock, CreditCard, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { PLAN_COLORS, PLAN_OPTIONS, STATUS_COLORS } from './constants';
import { Section } from './shared';

export interface PlanTabProps {
  orgId: string;
  ledger: AdminOrganizationDetail['ledger'];
  onLedgerChange: () => void;
}

export function PlanTab({ orgId, ledger, onLedgerChange }: PlanTabProps) {
  // A7Y-1833 — the effective (entitled) plan is the grant-only max of the
  // billed plan and any override; the override never reduces access below the
  // paid plan. The "Subscription Plan" / "Override Plan" rows below still show
  // the raw billed vs override values.
  const effectivePlanId = maxPlanTier(ledger?.planId, ledger?.overridePlanId);
  const effectivePlan = PLAN_TIERS[effectivePlanId] ?? PLAN_TIERS.free;

  return (
    <div className="space-y-8">
      {/* Plan Info */}
      <Section title="Plan" icon={<CreditCard className="h-5 w-5" />}>
        {ledger ? (
          <DescriptionList>
            <DescriptionTerm>Subscription Plan</DescriptionTerm>
            <DescriptionDetails>
              <Badge color={PLAN_COLORS[ledger.planId ?? 'free'] ?? 'zinc'}>
                {(ledger.planId ?? 'free').toUpperCase()}
              </Badge>
            </DescriptionDetails>

            <DescriptionTerm>Override Plan</DescriptionTerm>
            <DescriptionDetails>
              {ledger.overridePlanId ? (
                <Badge color="amber">{ledger.overridePlanId.toUpperCase()}</Badge>
              ) : (
                <Text className="text-zinc-400">None</Text>
              )}
            </DescriptionDetails>

            <DescriptionTerm>Billing Status</DescriptionTerm>
            <DescriptionDetails>
              <Badge color={STATUS_COLORS[ledger.status ?? ''] ?? 'zinc'}>
                {(ledger.status ?? 'unknown').toUpperCase().replace('_', ' ')}
              </Badge>
            </DescriptionDetails>

            <DescriptionTerm>Billing Cadence</DescriptionTerm>
            <DescriptionDetails className="capitalize">
              {ledger.billingCadence ?? '\u2014'}
            </DescriptionDetails>

            <DescriptionTerm>Currency</DescriptionTerm>
            <DescriptionDetails className="uppercase">
              {ledger.currency ?? '\u2014'}
            </DescriptionDetails>

            {ledger.stripeCustomerId && (
              <>
                <DescriptionTerm>Stripe Customer</DescriptionTerm>
                <DescriptionDetails>
                  <code className="rounded bg-zinc-100 dark:bg-zinc-700 px-1.5 py-0.5 text-sm">
                    {ledger.stripeCustomerId}
                  </code>
                </DescriptionDetails>
              </>
            )}

            {ledger.stripeSubscriptionId && (
              <>
                <DescriptionTerm>Stripe Subscription</DescriptionTerm>
                <DescriptionDetails>
                  <code className="rounded bg-zinc-100 dark:bg-zinc-700 px-1.5 py-0.5 text-sm">
                    {ledger.stripeSubscriptionId}
                  </code>
                </DescriptionDetails>
              </>
            )}

            {ledger.renewalDate && (
              <>
                <DescriptionTerm>Renewal Date</DescriptionTerm>
                <DescriptionDetails>{formatDate(ledger.renewalDate)}</DescriptionDetails>
              </>
            )}

            {ledger.trialEndsAt && (
              <>
                <DescriptionTerm>Trial Ends</DescriptionTerm>
                <DescriptionDetails>{formatDate(ledger.trialEndsAt)}</DescriptionDetails>
              </>
            )}

            {ledger.graceExpiresAt && (
              <>
                <DescriptionTerm>Grace Expires</DescriptionTerm>
                <DescriptionDetails>{formatDate(ledger.graceExpiresAt)}</DescriptionDetails>
              </>
            )}

            <DescriptionTerm>Max Projects</DescriptionTerm>
            <DescriptionDetails>
              {ledger.maxActiveProjects === -1 ? 'Unlimited' : ledger.maxActiveProjects}
              {ledger.addonExtraProjectQuantity > 0 && (
                <Text className="ml-1 text-zinc-400">
                  (+{ledger.addonExtraProjectQuantity} addon)
                </Text>
              )}
            </DescriptionDetails>
          </DescriptionList>
        ) : (
          <Text className="text-zinc-400">No billing ledger found for this organization.</Text>
        )}
      </Section>

      {/* Plan Entitlements (read-only) */}
      <PlanEntitlementsSection
        effectivePlanId={effectivePlanId}
        features={effectivePlan.features}
      />

      {/* Override Plan Form */}
      <OverridePlanForm
        orgId={orgId}
        currentOverride={ledger?.overridePlanId ?? null}
        onSuccess={onLedgerChange}
      />

      {/* Free Trial Form */}
      <FreeTrialForm
        orgId={orgId}
        currentTrialEndsAt={ledger?.trialEndsAt ?? null}
        onSuccess={onLedgerChange}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function OverridePlanForm({
  orgId,
  currentOverride,
  onSuccess,
}: {
  orgId: string;
  currentOverride: PlanId | null | undefined;
  onSuccess: () => void;
}) {
  const [selectedPlan, setSelectedPlan] = useState(currentOverride ?? '');

  const mutation = useMutation({
    mutationFn: () =>
      api.setOverridePlan(orgId, selectedPlan === '' ? null : (selectedPlan as PlanId)),
    onSuccess,
  });

  const hasChanged = (selectedPlan || null) !== (currentOverride || null);

  return (
    <Section title="Override Plan" icon={<CreditCard className="h-5 w-5" />}>
      <Text className="mb-4 text-sm text-zinc-500 dark:text-zinc-400">
        When an override plan is set, the organization receives entitlements for the higher of its
        subscription plan and this override — the override never reduces access below the paid plan,
        and it does not change Stripe billing (the org keeps paying its real subscription). Use this
        to grant free or elevated access.
      </Text>
      <Fieldset>
        <Field>
          <Label>Override Plan</Label>
          <Select
            value={selectedPlan}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedPlan(e.target.value)}
          >
            {PLAN_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </Field>
        <div className="mt-4 flex items-center gap-3">
          <Button
            color="indigo"
            disabled={!hasChanged || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Saving...' : 'Save Override'}
          </Button>
          {mutation.isSuccess && (
            <Text className="text-sm text-green-600">Override updated successfully.</Text>
          )}
          {mutation.isError && (
            <Text className="text-sm text-red-600">
              Failed to update override. {(mutation.error as Error)?.message}
            </Text>
          )}
        </div>
      </Fieldset>
    </Section>
  );
}

function toDateInputValue(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function FreeTrialForm({
  orgId,
  currentTrialEndsAt,
  onSuccess,
}: {
  orgId: string;
  currentTrialEndsAt: string | null;
  onSuccess: () => void;
}) {
  const [selectedDate, setSelectedDate] = useState(toDateInputValue(currentTrialEndsAt));
  const confirm = useConfirm();

  const mutation = useMutation({
    mutationFn: () => {
      const trialEndsAt = selectedDate
        ? new Date(selectedDate + 'T23:59:59.999Z').toISOString()
        : null;
      return api.setTrialEndDate(orgId, trialEndsAt);
    },
    onSuccess,
  });

  const hasChanged = selectedDate !== toDateInputValue(currentTrialEndsAt);

  return (
    <Section title="Free Trial" icon={<Clock className="h-5 w-5" />}>
      <Text className="mb-4 text-sm text-zinc-500 dark:text-zinc-400">
        Set or extend the free trial end date for this organization. Clearing the date removes the
        trial period.
      </Text>
      <Fieldset>
        <Field>
          <Label>Trial End Date</Label>
          {currentTrialEndsAt && (
            <Text className="mb-1 text-xs text-zinc-400">
              Current: {new Date(currentTrialEndsAt).toLocaleDateString()}
            </Text>
          )}
          <Input
            type="date"
            value={selectedDate}
            // "No earlier than tomorrow" is defined against the current clock;
            // pinning it at mount would let a long-open tab offer a past date.
            // eslint-disable-next-line react-hooks/purity
            min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSelectedDate(e.target.value)}
          />
        </Field>
        <div className="mt-4 flex items-center gap-3">
          <Button
            color="indigo"
            disabled={!hasChanged || mutation.isPending}
            onClick={async () => {
              const trialEndsAt = selectedDate ? new Date(selectedDate + 'T23:59:59.999Z') : null;
              const ok = await confirm(
                trialEndsAt
                  ? {
                      title: 'Update trial end date?',
                      description: `The organization's free trial will end on ${trialEndsAt.toLocaleString()}.`,
                      confirmLabel: 'Update',
                    }
                  : {
                      title: 'Remove trial end date?',
                      description: "The organization's free trial period will be removed.",
                      confirmLabel: 'Remove',
                      confirmColor: 'red',
                    }
              );
              if (ok) mutation.mutate();
            }}
          >
            {mutation.isPending ? 'Saving...' : 'Save Trial Date'}
          </Button>
          {selectedDate && (
            <Button plain disabled={mutation.isPending} onClick={() => setSelectedDate('')}>
              Clear
            </Button>
          )}
          {mutation.isSuccess && (
            <Text className="text-sm text-green-600">Trial date updated successfully.</Text>
          )}
          {mutation.isError && (
            <Text className="text-sm text-red-600">
              Failed to update trial date. {(mutation.error as Error)?.message}
            </Text>
          )}
        </div>
      </Fieldset>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Plan Entitlements (read-only preview of PLAN_TIERS[effectivePlanId].features)
// ---------------------------------------------------------------------------

const AUDIT_CADENCE_LABEL: Record<PlanFeatures['maxAuditFrequency'], string> = {
  manual_only: 'Manual only',
  monthly: 'Monthly',
  biweekly: 'Biweekly',
  weekly: 'Weekly',
  '2x_week': '2x/week',
  daily: 'Daily',
};

const ASSET_GEN_LABEL: Record<PlanFeatures['assetGenerationLevel'], string> = {
  none: 'None',
  text: 'Text only',
  text_and_images: 'Text and images',
};

function formatUnlimited(value: number): string {
  return value === -1 ? 'Unlimited' : String(value);
}

function formatRetention(days: number): string {
  if (days === -1) return 'Unlimited';
  return `${days} day${days === 1 ? '' : 's'}`;
}

function formatCompetitors(value: number | null): string {
  if (value == null) return 'Unlimited';
  return String(value);
}

function formatReviewsLookback(days: number | null): string {
  if (days == null) return 'None';
  if (days === -1) return 'Unlimited';
  return `${days} day${days === 1 ? '' : 's'}`;
}

function formatLlms(llms: string[]): string {
  if (!llms.length) return 'None';
  return llms.join(', ');
}

function enabledModules(features: PlanFeatures): string[] {
  const modules: string[] = [];
  // Core static modules surfaced in every audit
  modules.push('Store text');
  modules.push('Screenshots');
  modules.push('Icon');
  if (features.includesReviewSentiment) modules.push('Reviews & sentiment');
  if (features.includesKeywordAnalysis) modules.push('Keyword analysis');
  if (features.aiDiscoverabilityLLMs.length > 0) modules.push('AI discoverability');
  if (features.includesCompetitorDiscovery) modules.push('Competitor discovery');
  if (features.includesIntentMapping) modules.push('Intent mapping');
  if (features.includesPolicyChecks) modules.push('Policy checks');
  return modules;
}

function YesNo({ value }: { value: boolean }) {
  return value ? <Badge color="green">Yes</Badge> : <Badge color="zinc">No</Badge>;
}

function PlanEntitlementsSection({
  effectivePlanId,
  features,
}: {
  effectivePlanId: PlanTier;
  features: PlanFeatures;
}) {
  const modules = enabledModules(features);

  return (
    <Section title="Plan Entitlements" icon={<ShieldCheck className="h-5 w-5" />}>
      <Text className="mb-4 text-sm text-zinc-500 dark:text-zinc-400">
        Read-only preview of entitlements for the effective plan{' '}
        <Badge color={PLAN_COLORS[effectivePlanId] ?? 'zinc'}>
          {effectivePlanId.toUpperCase()}
        </Badge>
        . Sourced from{' '}
        <code className="rounded bg-zinc-100 dark:bg-zinc-700 px-1 py-0.5 text-xs">PLAN_TIERS</code>
        .
      </Text>
      <DescriptionList>
        <DescriptionTerm>Projects limit</DescriptionTerm>
        <DescriptionDetails>{formatUnlimited(features.maxProjects)}</DescriptionDetails>

        <DescriptionTerm>Target Languages per project</DescriptionTerm>
        <DescriptionDetails>{formatUnlimited(features.maxMarketsPerProject)}</DescriptionDetails>

        <DescriptionTerm>Team seats</DescriptionTerm>
        <DescriptionDetails>{formatUnlimited(features.maxTeamSeats)}</DescriptionDetails>

        <DescriptionTerm>Audit cadence</DescriptionTerm>
        <DescriptionDetails>{AUDIT_CADENCE_LABEL[features.maxAuditFrequency]}</DescriptionDetails>

        <DescriptionTerm>Data retention</DescriptionTerm>
        <DescriptionDetails>{formatRetention(features.dataRetentionDays)}</DescriptionDetails>

        <DescriptionTerm>Competitors per project</DescriptionTerm>
        <DescriptionDetails>
          {formatUnlimited(features.maxCompetitorsPerProject)}
        </DescriptionDetails>

        <DescriptionTerm>Total competitors</DescriptionTerm>
        <DescriptionDetails>{formatCompetitors(features.maxTotalCompetitors)}</DescriptionDetails>

        <DescriptionTerm>Module access</DescriptionTerm>
        <DescriptionDetails>
          <ul className="list-disc list-inside space-y-0.5">
            {modules.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </DescriptionDetails>

        <DescriptionTerm>Reviews lookback</DescriptionTerm>
        <DescriptionDetails>
          {formatReviewsLookback(features.reviewsLookbackDays)}
        </DescriptionDetails>

        <DescriptionTerm>AI discovery LLMs</DescriptionTerm>
        <DescriptionDetails>{formatLlms(features.aiDiscoverabilityLLMs)}</DescriptionDetails>

        <DescriptionTerm>Slack integration</DescriptionTerm>
        <DescriptionDetails>
          <YesNo value={features.includesSlackIntegration} />
        </DescriptionDetails>

        <DescriptionTerm>Figma plugin</DescriptionTerm>
        <DescriptionDetails>
          <YesNo value={features.includesFigmaPlugin} />
        </DescriptionDetails>

        <DescriptionTerm>API access</DescriptionTerm>
        <DescriptionDetails>
          <YesNo value={features.includesApiAccess} />
        </DescriptionDetails>

        <DescriptionTerm>Asset generation</DescriptionTerm>
        <DescriptionDetails>{ASSET_GEN_LABEL[features.assetGenerationLevel]}</DescriptionDetails>
      </DescriptionList>
    </Section>
  );
}
